import {
  IconButton as MuiIconButton,
  type IconButtonProps as MuiIconButtonProps,
  type SxProps,
  type Theme,
} from "@mui/material";

import { type IconButtonSize, type IconButtonVariant } from "../../../ui-theme";

export interface BaseIconButtonProps extends Omit<
  MuiIconButtonProps,
  "color" | "invert" | "outline-thickness"
> {
  variant: IconButtonVariant;
  invert?: boolean;
  size?: IconButtonSize;
  // for some reason, MUI's IconButton props doesn't include href. However, when passed down to the component, it works as expected
  href?: string;
  /**
   * Sets aria-selected to true. This is useful when you need to force visual active state. We do
   * this for some flows that buttons look selected when some options were picked by user (e.g. when
   * user sets a filter in shift discovery, the button in header looks selected).
   */
  isSelected?: boolean;
  sx?: SxProps<Theme>;
  LinkComponent: React.ElementType;
}

const iconButtonVariantOverrides: Record<IconButtonVariant, MuiIconButtonProps["color"]> = {
  contained: "primary",
  outlined: "secondary",
  outlinedHeavy: "secondary",
  ghost: "info",
};

const iconButtonOutlineThicknessOverrides: Record<
  IconButtonVariant,
  MuiIconButtonProps["outline-thickness"]
> = {
  contained: undefined,
  ghost: undefined,
  outlined: "regular",
  outlinedHeavy: "thick",
};

/**
 * This component is internal for the component library. It allows to pass any children
 * so we can use it for IconButton and LoadingIconButton - consumers of these two are not allowed
 * to pass any children as we limit them to either our icon or loading spinner
 *
 * A wrapper around MuiIconButton that limits variants to our custom ones.
 *
 * NOTE: Provide `href` to make the button render as an anchor tag. It will support react-router
 * redirects.
 */

export function BaseIconButton(props: BaseIconButtonProps) {
  const {
    variant = "contained",
    size = "medium",
    onClick,
    isSelected,
    "aria-selected": ariaSelected,
    invert,
    LinkComponent,
    ...restProps
  } = props;

  return (
    <MuiIconButton
      outline-thickness={iconButtonOutlineThicknessOverrides[variant]}
      aria-selected={ariaSelected ?? isSelected}
      invert={invert ? "true" : undefined}
      color={iconButtonVariantOverrides[variant]}
      size={size}
      LinkComponent={LinkComponent}
      onClick={onClick}
      {...restProps}
    />
  );
}
